import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import Reveal from "@/components/Reveal";
import ProjectModal from "@/components/ProjectModal";
import { imgSrc } from "@/components/Rail";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const spans = ["md:col-span-7", "md:col-span-5", "md:col-span-4", "md:col-span-4", "md:col-span-4", "md:col-span-5", "md:col-span-7", "md:col-span-6", "md:col-span-6", "md:col-span-12"];

const Portfolio = () => {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/projects`)
      .then(({ data }) => {
        if (Array.isArray(data) && data.length) setItems(data.map((d) => ({ ...d, img: imgSrc(d.image) })));
      })
      .catch(() => {});
  }, []);

  const cats = ["all", ...new Set(items.map((i) => i.cat))];
  const visible = items.filter((i) => filter === "all" || i.cat === filter);

  return (
    <section id="work" data-testid="portfolio-section" className="py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-clay">{t.portfolio.overline}</p>
          <h2 data-testid="portfolio-heading" className="mt-6 max-w-3xl font-serif text-4xl tracking-tight text-ink sm:text-5xl">
            {t.portfolio.h2a} <em className="italic text-clay">{t.portfolio.h2b}</em>
          </h2>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink/60">{t.portfolio.sub}</p>
        </Reveal>

        {items.length > 0 && (
          <Reveal delay={0.1}>
            <div data-testid="portfolio-filters" className="mt-12 flex flex-wrap gap-3">
              {cats.map((c) => (
                <button
                  key={c}
                  onClick={() => setFilter(c)}
                  data-testid={`filter-${c.toLowerCase().replace(/[^a-z0-9]+/gi, "-")}`}
                  className={`rounded-full border px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] transition-colors duration-300 ${
                    filter === c ? "border-ink bg-ink text-bone" : "border-ink/20 text-ink/60 hover:border-clay hover:text-clay"
                  }`}
                >
                  {c === "all" ? t.portfolio.filters[0] : c}
                </button>
              ))}
            </div>
          </Reveal>
        )}

        <motion.div layout className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-12">
          <AnimatePresence mode="popLayout">
            {visible.map((it, i) => (
              <motion.button
                layout
                key={it.id || it.name}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                onClick={() => setSelected(it)}
                data-testid={`portfolio-item-${(it.name || "").toLowerCase().replace(/[^a-z0-9]+/gi, "-")}`}
                className={`group relative h-80 cursor-pointer overflow-hidden bg-sand text-start ${spans[i % spans.length]}`}
                style={{ perspective: "1000px" }}
              >
                <div className="h-full w-full transition-transform duration-700 ease-out [transform:perspective(1000px)_rotateY(14deg)] group-hover:[transform:perspective(1000px)_rotateY(0deg)_scale(1.03)]">
                  <img src={it.img} alt={it.name} loading="lazy" className="h-full w-full object-cover" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent opacity-80 transition-opacity duration-500 group-hover:opacity-100" />
                <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-6">
                  <div>
                    <p className="font-serif text-2xl tracking-tight text-bone">{it.name}</p>
                    <p className="mt-1 text-xs uppercase tracking-[0.18em] text-bone/70">{it.client}</p>
                  </div>
                  <span className="rounded-full border border-bone/40 px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-bone opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                    {t.portfolio.view}
                  </span>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <ProjectModal project={selected} img={selected?.img} onClose={() => setSelected(null)} />
    </section>
  );
};

export default Portfolio;
